/*
 * Treat — product card titles on ONE line on desktop (Theme settings > Product card > Text >
 * "Fit title on one line (desktop)").
 *
 * The design sets every card title on a single line. Real names are longer than the mockup's
 * ("CLEANSER / THE LEAD TO BRIGHT"), so instead of wrapping, the title font shrinks until the
 * name fits. It shrinks per GROUP, not per card: every card in one grid or carousel gets the
 * same size, the size the longest name needs, so a row never mixes title sizes.
 *
 * - Desktop only (>= 990px). Below that the title wraps and is clamped to "Title lines".
 * - Never below MIN_SCALE of the theme's size; a name that still does not fit is cut with an
 *   ellipsis rather than dragging the whole row down to an unreadable size.
 * - The size is set inline with !important because the typography scale classes (.type-*) are
 *   !important themselves (MERCHANT-TYPOGRAPHY-SCALE); inline !important is the only thing that
 *   outranks them, and it is removed again whenever the viewport leaves desktop.
 * - Re-fits on resize, after web fonts load (the brand font is wider than the fallback), and
 *   when cards arrive later (related products are fetched, the Theme Editor re-renders sections).
 */
(function treatCardTitleFit() {
  if (window.TreatCardTitleFit) return;

  const MIN_SCALE = 0.65;
  const CARD_SELECTOR = '.treat-card[data-treat-title-fit]';
  const GROUP_SELECTOR = 'ul, .grid, .product-grid, .slider';
  const desktop = window.matchMedia('(min-width: 990px)');
  const observed = new WeakSet();
  let frame = null;

  function groupsOnPage() {
    const groups = new Map();
    document.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      const group = card.closest(GROUP_SELECTOR) || card.parentElement;
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push(card);
    });
    return groups;
  }

  function reset(link) {
    link.style.removeProperty('font-size');
  }

  function fitGroup(cards) {
    const links = cards.map((card) => card.querySelector('.card__heading a')).filter(Boolean);
    if (!links.length) return;

    links.forEach(reset);
    if (!desktop.matches) return;

    // Measured at the theme's own size: natural one-line width against the width available.
    let scale = 1;
    links.forEach((link) => {
      const available = link.parentElement.clientWidth;
      const natural = link.scrollWidth;
      if (available > 0 && natural > available) scale = Math.min(scale, available / natural);
    });
    if (scale >= 1) return;

    scale = Math.max(MIN_SCALE, Math.floor(scale * 1000) / 1000);
    links.forEach((link) => {
      const base = parseFloat(window.getComputedStyle(link).fontSize);
      link.style.setProperty('font-size', `${(base * scale).toFixed(2)}px`, 'important');
    });
  }

  function fitAll() {
    frame = null;
    groupsOnPage().forEach((cards, group) => {
      if (group && !observed.has(group)) {
        observed.add(group);
        resizeObserver?.observe(group);
      }
      fitGroup(cards);
    });
  }

  // A short timeout, not requestAnimationFrame: rAF never fires in a background tab, so a page
  // opened in one showed wrapped/cut titles until something else triggered a re-fit.
  function schedule() {
    if (frame) return;
    frame = window.setTimeout(fitAll, 30);
  }

  // Width only: re-fitting changes the title size and so the group's HEIGHT, and reacting to
  // that would re-fit on every frame.
  const widths = new WeakMap();
  const resizeObserver =
    'ResizeObserver' in window
      ? new ResizeObserver((entries) => {
          let changed = false;
          entries.forEach((entry) => {
            const width = Math.round(entry.contentRect.width);
            if (widths.get(entry.target) !== width) {
              widths.set(entry.target, width);
              changed = true;
            }
          });
          if (changed) schedule();
        })
      : null;

  desktop.addEventListener('change', schedule);
  window.addEventListener('resize', schedule);
  document.fonts?.ready.then(schedule);
  document.addEventListener('shopify:section:load', schedule);

  new MutationObserver((mutations) => {
    const added = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) => node.nodeType === 1 && (node.matches(CARD_SELECTOR) || node.querySelector(CARD_SELECTOR))
      )
    );
    if (added) schedule();
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }

  window.TreatCardTitleFit = { refresh: schedule };
})();
